import { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { t, getLocale, setLocale } from '../i18n';

export default function AuthPage({ mode = 'login' }) {
  const { login, signup } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefillEmail = searchParams.get('email') || '';
  const [form, setForm] = useState({ name: '', email: prefillEmail, password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showMagicLink, setShowMagicLink] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const isSignup = mode === 'signup';

  const handle = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);
    try {
      if (isSignup) {
        const result = await signup(form.email, form.password, form.name);
        setSuccessMessage(result.message);
        setForm({ name: '', email: '', password: '' });
      } else {
        const loggedInUser = await login(form.email, form.password);
        navigate(loggedInUser.must_change_password ? '/setup' : '/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!form.email) {
      setError(t('pleaseEnterEmail'));
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setSuccessMessage(data.message);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    if (!form.email) {
      setError(t('pleaseEnterEmail'));
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/auth/magic-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setSuccessMessage(data.message);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Nav — same as landing page */}
      <nav style={{ padding: '0 40px', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', background: 'white', flexShrink: 0 }}>
        <Link to="/" style={{ fontFamily: 'Playfair Display', fontSize: 22, fontWeight: 700, color: 'var(--match-a)', textDecoration: 'none' }}>
          {t('brand')}
        </Link>
        <button
          className="btn btn-ghost"
          style={{ fontSize: 14 }}
          onClick={() => setLocale(getLocale() === 'en' ? 'ja' : 'en')}
        >
          {getLocale() === 'en' ? '日本語' : 'EN'}
        </button>
      </nav>

      {/* Two-panel layout below nav */}
      <div style={{ flex: 1, display: 'flex', background: 'linear-gradient(135deg, #f0fdf4 0%, #fafdf5 60%, #dcfce7 100%)' }}>
        {/* Left panel - branding */}
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center',
          padding: '60px', maxWidth: 480,
          background: 'linear-gradient(160deg, #3d5429 0%, #5a7a3a 100%)',
          color: 'white'
        }} className="auth-panel">
          <Link to="/" style={{ fontSize: 56, marginBottom: 16, display: 'block', textDecoration: 'none' }}>🍵</Link>
          <h1 style={{ fontSize: 40, fontFamily: 'Playfair Display', fontWeight: 700, marginBottom: 16, lineHeight: 1.2 }}>
            {t('authTagline')}
          </h1>
          <p style={{ fontSize: 18, opacity: 0.85, lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: t('authTaglineSubtitle') }} />
          <div style={{ marginTop: 40, display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[t('authStep1'), t('authStep2'), t('authStep3'), t('authStep4')].map((step, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: 'rgba(255,255,255,0.2)', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 700, flexShrink: 0
                }}>{i + 1}</div>
                <span style={{ fontSize: 15, opacity: 0.9 }}>{step}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right panel - form */}
        <div className="auth-form-panel" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
          <div className="card auth-form-card" style={{ width: '100%', maxWidth: 420, padding: 40 }}>
          <h2 style={{ fontSize: 26, marginBottom: 8 }}>
            {isSignup ? t('createAccount') : t('welcomeBack')}
          </h2>
          <p style={{ color: 'var(--text-muted)', marginBottom: 28, fontSize: 15 }}>
            {isSignup ? t('startConnecting') : t('signInToSee')}
          </p>

          {error && (
            <div style={{
              background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca',
              borderRadius: 8, padding: '10px 14px', fontSize: 14, marginBottom: 20
            }}>
              {error === 'Email already in use' ? (
                <>Email already in use — please <Link to="/login" style={{ color: '#dc2626', fontWeight: 600 }}>sign in</Link></>
              ) : error}
            </div>
          )}

          {successMessage && (
            <div style={{
              background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0',
              borderRadius: 8, padding: '10px 14px', fontSize: 14, marginBottom: 20
            }}>{successMessage}</div>
          )}

          <form onSubmit={showForgotPassword ? handleForgotPassword : showMagicLink ? handleMagicLink : handle} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {isSignup && (
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>
                  {t('fullName')}
                </label>
                <input
                  className="input"
                  placeholder={t('fullName')}
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>
            )}
            <div>
              <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>
                {t('email')}
              </label>
              <input
                className="input"
                type="email"
                placeholder={t('email')}
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                required
              />
            </div>
            {!showMagicLink && !showForgotPassword && (
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>
                  {t('password')}
                </label>
                <input
                  className="input"
                  type="password"
                  placeholder="••••••••"
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  required={!showMagicLink && !showForgotPassword}
                  minLength={6}
                />
              </div>
            )}
            <button
              type="submit"
              className="btn btn-primary"
              style={{ marginTop: 8, justifyContent: 'center', padding: '12px' }}
              disabled={loading}
            >
              {loading ? '...' : isSignup ? t('createAccountBtn') : showForgotPassword ? t('sendResetLink') : showMagicLink ? t('sendMagicLink') : t('signInBtn')}
            </button>
          </form>

          {!isSignup && !showMagicLink && !showForgotPassword && (
            <div style={{ display: 'flex', gap: 16, marginTop: 16, flexWrap: 'wrap' }}>
              <button
                onClick={() => { setShowMagicLink(true); setError(''); setSuccessMessage(''); }}
                style={{
                  background: 'none', border: 'none', color: 'var(--match-a)',
                  fontSize: 14, cursor: 'pointer', textDecoration: 'underline', padding: 0
                }}
              >
                {t('useMagicLink')}
              </button>
              <button
                onClick={() => { setShowForgotPassword(true); setError(''); setSuccessMessage(''); }}
                style={{
                  background: 'none', border: 'none', color: 'var(--text-muted)',
                  fontSize: 14, cursor: 'pointer', textDecoration: 'underline', padding: 0
                }}
              >
                {t('resetPasswordLink')}
              </button>
            </div>
          )}

          {!isSignup && (showMagicLink || showForgotPassword) && (
            <button
              onClick={() => { setShowMagicLink(false); setShowForgotPassword(false); setError(''); setSuccessMessage(''); }}
              style={{
                marginTop: 8, background: 'none', border: 'none',
                color: 'var(--text-muted)', fontSize: 14, cursor: 'pointer', textDecoration: 'underline'
              }}
            >
              {t('usePasswordInstead')}
            </button>
          )}

          <p style={{ marginTop: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>
            {t('dontHaveAccount')}{' '}
            <Link to="/request-access" style={{ color: 'var(--match-a)', fontWeight: 600 }}>
              {t('requestAccess')}
            </Link>
          </p>
          </div>
        </div>
      </div>
    </div>
  );
}
